import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n";
import { Zap } from "./common";
import ElectricDust from "./ElectricDust";
import { wa, DEFAULT_MSG_PT, DEFAULT_MSG_EN, scrollToId, IMAGES, PHONE_DISPLAY } from "../lib/site";

const lineEase = [0.16, 1, 0.3, 1];

export default function Hero() {
    const { lang, t } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yImg = useTransform(scrollYProgress, [0, 1], [0, 70]);
    const yBig = useTransform(scrollYProgress, [0, 1], [0, -50]);

    const msg = lang === "pt" ? DEFAULT_MSG_PT : DEFAULT_MSG_EN;

    return (
        <section
            id="inicio"
            ref={ref}
            className="relative min-h-screen flex flex-col justify-end overflow-hidden grid-bg"
        >
            <ElectricDust />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(255,230,0,0.07),transparent_55%)] pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-b from-obsidian/40 via-transparent to-obsidian pointer-events-none" />

            <div className="relative max-w-7xl mx-auto w-full px-5 md:px-10 pt-32 md:pt-40 pb-14 grid lg:grid-cols-[minmax(0,1.12fr)_minmax(0,0.88fr)] gap-14 items-end">
                <motion.div style={{ y: yBig }}>
                    <div className="overflow-hidden mb-7">
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1, ease: lineEase }}
                            className="inline-flex items-center gap-2.5 rounded-full border border-volt/30 bg-volt/5 px-4 py-1.5"
                        >
                            <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" />
                            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-slate-300">
                                {t.hero.badge}
                            </span>
                        </motion.div>
                    </div>

                    <h1 className="font-poster uppercase leading-[1.02] tracking-[0.015em] text-[15vw] md:text-[11vw] lg:text-[7.2vw] xl:text-[6.2rem]">
                        {t.hero.lines.map((line, i) => (
                            <span key={`${line}-${i}`} className="block overflow-hidden pb-1">
                                <motion.span
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        duration: 1,
                                        delay: 0.25 + i * 0.15,
                                        ease: lineEase,
                                    }}
                                    className={`block ${
                                        i === 1
                                            ? "text-volt flicker-on drop-shadow-[0_0_35px_rgba(255,230,0,0.35)]"
                                            : ""
                                    }`}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.85, ease: lineEase }}
                        className="mt-6 max-w-xl text-slate-300 text-base md:text-lg leading-relaxed"
                    >
                        {t.hero.sub}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1, ease: lineEase }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <a
                            data-testid="hero-whatsapp-cta"
                            href={wa(msg)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-center gap-3 bg-volt text-obsidian font-bold px-7 py-4 rounded-full text-sm md:text-base hover:shadow-[0_0_40px_rgba(255,230,0,0.45)] hover:-translate-y-0.5 transition-all"
                        >
                            <Zap className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                            {t.hero.cta1}
                        </a>
                        <button
                            data-testid="hero-3d-cta"
                            onClick={() => scrollToId("spda")}
                            className="inline-flex items-center gap-3 border border-white/20 text-white font-semibold px-7 py-4 rounded-full text-sm md:text-base hover:border-volt hover:text-volt transition-all"
                        >
                            {t.hero.cta2}
                            <span className="font-mono text-xs opacity-60">3D</span>
                        </button>
                    </motion.div>
                </motion.div>

                <motion.div style={{ y: yImg }} className="relative hidden lg:block">
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1.1, delay: 0.55, ease: lineEase }}
                        className="relative clip-notch border border-white/10"
                    >
                        <img
                            src={IMAGES.hero}
                            alt="São Paulo à noite"
                            className="w-full h-[520px] object-cover saturate-[0.4] contrast-125"
                            loading="eager"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/30" />
                        <div className="absolute inset-0 bg-volt/10 mix-blend-overlay" />
                        <div className="absolute top-0 right-0 w-14 h-14 border-t-2 border-r-2 border-volt" />
                        <div className="absolute bottom-0 left-0 w-14 h-14 border-b-2 border-l-2 border-volt" />

                        <div className="absolute top-6 left-6 bg-obsidian/80 backdrop-blur-md border border-volt/30 rounded-lg px-4 py-2.5">
                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-volt">
                                {t.hero.chipTop}
                            </p>
                        </div>
                        <div className="absolute bottom-6 right-6 bg-obsidian/80 backdrop-blur-md border border-white/15 rounded-lg px-4 py-2.5">
                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-200 flex items-center gap-2">
                                <Zap className="w-3 h-3 text-volt" />
                                {t.hero.chipBottom}
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.2 }}
                className="relative border-t border-white/10"
            >
                <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-2 lg:grid-cols-4">
                    {t.hero.metrics.map((m, i) => (
                        <div
                            key={m}
                            className="flex items-start gap-3 py-5 pr-6 border-r border-white/5 last:border-0"
                        >
                            <span className="font-mono text-[10px] text-volt mt-0.5">
                                0{i + 1}
                            </span>
                            <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.14em] text-slate-400 leading-relaxed">
                                {m}
                            </span>
                        </div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
}
